import { UNKNOWN_ERROR } from 'utils/errors';
import { config } from 'api';
import { refreshApp } from 'apps/root/actions/root';
import { fetchBasket } from 'apps/basket/actions/basket';
import { addQueryParams } from 'utils/url';
import errorHandler from 'lib/utils/errorHandler';

export const REQUEST_LOGOUT = 'REQUEST_LOGOUT';
export const RECEIVE_LOGOUT = 'RECEIVE_LOGOUT';
export const LOGOUT_ERROR = 'LOGOUT_ERROR';

function requestLogout() {
  return { type: REQUEST_LOGOUT };
}

function receiveLogout() {
  return { type: RECEIVE_LOGOUT };
}

function logoutError(error) {
  return { type: LOGOUT_ERROR, error };
}

export function redirectAfterLogout(url, origin, network) {
  const remoteLogoutUrl = addQueryParams(url, {
    client_id: 'webshop-ocean',
    post_logout_redirect_uri: `${origin}/webshop/sso/post-logout.go`,
  });
  return network.redirectTo({ to: remoteLogoutUrl });
}

function getSsoUrls() {
  return (dispatch, getState, { network }) =>
    network
      .get(`${config.apiBasePath}/sso/urls`);
}

export function logout(redirectUrl = '/') {
  return (dispatch, getState, { network, router }) => {
    dispatch(requestLogout());
    return network.post(`${config.apiBasePath}/auth/logout`)
      .then((result) => {
        dispatch(receiveLogout());
        if (result.shouldLogoutFromSSO) {
          const state = getState();
          const { http: { origin } } = state;
          if (!config.ssoAllowedUrlOverriding) {
            return redirectAfterLogout(`${config.ssoTargetOrigin}/auth-service/sso/remote-logout`, origin, network);
          }
          return dispatch(getSsoUrls()).then((response) =>
            redirectAfterLogout(response.endSessionEndpointUrl, origin, network));
        }

        dispatch(refreshApp(() => {
          router.push(redirectUrl);
        }));
        return dispatch(fetchBasket());
      })
      .catch(err => {
        if (!err.data) {
          dispatch(logoutError(UNKNOWN_ERROR.error));
        } else {
          dispatch(logoutError(err.data.message));
        }
      });
  };
}

export function getAuthParams(nonce, state, redirectUri, postLoginUri) {
  return (dispatch, getState, { network }) =>
    network.post(`${config.apiBasePath}/sso/auth-request-params`, {
      body: JSON.stringify({
        nonce,
        state,
        redirectUri,
        webshopPostLoginUri: postLoginUri,
      }),
    });
}

export function getSsoAuthorizationUrl(nonce, state, redirectUri, postLoginUri) {
  const defaultAuthorizationUrl = `${config.ssoTargetOrigin}/auth-service/sso/authorize`;
  return (dispatch) =>
    dispatch(getAuthParams(nonce, state, redirectUri, postLoginUri)).then(() => {
      if (!config.ssoAllowedUrlOverriding) {
        return defaultAuthorizationUrl;
      }
      return dispatch(getSsoUrls()).then(
        response => response.authorizationEndpointUrl,
        (response) => {
          // eslint-disable-next-line no-console
          console.error(
            `[getSsoAuthorizationUrl]: Error, endpoint ${config.apiBasePath}/sso/urls \
            responded with "status ${response.status}: ${response.statusText}"`
          );
          return defaultAuthorizationUrl;
        }
      );
    },
    (response) => {
      // eslint-disable-next-line no-console
      console.error(
        `[getSsoAuthorizationUrl]: Error, endpoint ${config.apiBasePath}/sso/auth-request-params \
        responded with "status ${response.status}: ${response.statusText}`);
      return defaultAuthorizationUrl;
    });
}

export function getSsoChangeCredentialsUrl() {
  const defaultCredentialsUrl = `${config.ssoTargetOrigin}/auth-service/sso/credentials`;
  return (dispatch) => {
    if (!config.ssoAllowedUrlOverriding) {
      return Promise.resolve(defaultCredentialsUrl);
    }

    return dispatch(getSsoUrls()).then(
      response => response.credentialsEndpointUrl,
      (response) => {
        // eslint-disable-next-line no-console
        console.error(
          `[getSsoAuthorizationUrl]: Error, endpoint ${config.apiBasePath}/sso/urls \
              responded with "status ${response.status}: ${response.statusText}"`
        );
        return defaultCredentialsUrl;
      }
    );
  };
}

export function getSsoCheckSessionUrl() {
  const defaultCheckSessionUrl = `${config.ssoTargetOrigin}/auth-service/sso/check-session`;
  return (dispatch) => {
    if (!config.ssoAllowedUrlOverriding) {
      return Promise.resolve(defaultCheckSessionUrl);
    }

    return dispatch(getSsoUrls()).then(
      (result) => result.checkSessionEndpointUrl,
      (response) => {
        // eslint-disable-next-line no-console
        console.error(
          `[getSsoCheckSessionUrl]: Error, endpoint ${config.apiBasePath}/sso/urls \
              responded with "status ${response.status}: ${response.statusText}"`
        );
        return defaultCheckSessionUrl;
      }
    );
  };
}

export const SET_FREE_HTML_SECTIONS = 'SET_FREE_HTML_SECTIONS';

export function setFreeHtmlSections(topFreeHtml, bottomFreeHtml) {
  return {
    type: SET_FREE_HTML_SECTIONS,
    topFreeHtml,
    bottomFreeHtml,
  };
}

export function ensureAlternativeRegistrationPageStaticContent() {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/direct-registration-sso-page`)
      .then((scribeDocument) => {
        const { topFreeHtml, bottomFreeHtml } = scribeDocument;
        dispatch(setFreeHtmlSections(topFreeHtml, bottomFreeHtml));
      })
      .catch(errorHandler);
}
